import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "reactfire";
import {
    Card,
    Spacer,
    Button,
    Input,
    Checkbox,
    CardHeader,
    CardBody,
    CardFooter,
    Divider,
    Link,
} from '@nextui-org/react';
import { Auth, createUserWithEmailAndPassword, GoogleAuthProvider, sendEmailVerification, signInWithPopup, updateProfile, UserCredential } from "firebase/auth";
import { setDoc, doc, collection, getFirestore, updateDoc } from "firebase/firestore";
import { Icon } from "@iconify/react";

export const Signup = () => {
    const [message, setMessage] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [name, setName] = useState("");
    const [company, setCompany] = useState("");
    const [jobTitle, setJobTitle] = useState("");
    const auth = useAuth();
    const navigate = useNavigate();
    const [user, setUser] = useState<UserCredential>();
    const [finalMessage, setFinalMessage] = useState("");
    const [isVisible, setIsVisible] = useState(false);
    const [isConfirmVisible, setIsConfirmVisible] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [agreedToTerms, setAgreedToTerms] = useState<boolean>(false); 

    const toggleVisibility = () => setIsVisible(!isVisible);
    const toggleConfirmVisibility = () => setIsConfirmVisible(!isConfirmVisible);

    useEffect(() => {
        if (user) {
            if (user.user.emailVerified) {
                setFinalMessage("Account created successfully. Redirecting to home...");
                setTimeout(() => {
                    navigate('/extractors');
                }, 3000);
            }
        }
    }, [navigate, user]);

    const onSignup = () => {
        setMessage("");
        if (!agreedToTerms) {
            setMessage("Please agree to the terms and conditions.");
            return;
        }
        if (password !== confirmPassword) {
            setMessage("Passwords do not match.");
            return;
        }
        if (password.length < 6) {
            setMessage("Password must be at least 6 characters long.");
            return;
        }
        createUserWithEmailAndPassword(auth, email, password)
            .then((userCredentials) => {
                // Signed in
                const firestore = getFirestore();
                setUser(userCredentials);
                setCurrentStep(1);
                setDoc(doc(collection(firestore, 'users'), userCredentials.user.uid), {
                    id: userCredentials.user.uid,
                    email: userCredentials.user.email,
                    usage: 0,
                    lastLoginDate: new Date().toISOString()
                });
            })
            .catch((error) => {
                const errorCode = error.code;
                const errorMessage = error.message;
                if (errorCode === 'auth/email-already-in-use') {
                    setMessage("Account already exists. Use Login");
                }
                console.log(errorCode, errorMessage)
            });

    }

    const saveProfile = async () => {

        setMessage("");

        if (user) {
            await updateProfile(user.user, {
                displayName: name,
            })

            updateDoc(doc(collection(getFirestore(), 'users'), user.user.uid), {
                displayName: name,
                company: company,
                jobTitle: jobTitle,
                lastLoginDate: new Date().toISOString()
            }).then(() => {
                if (user.user.emailVerified) {
                    navigate('/extractors');
                }
                else {
                    sendEmailVerification(user.user).then(() => {
                        setFinalMessage("Verification email sent. Please verify your email address.");
                    });
                }
            });
        }
    }

    const signInWithGoogle = async (auth: Auth) => {
        const provider = new GoogleAuthProvider();
        const userCredentials = await signInWithPopup(auth, provider);
        const firestore = getFirestore();
        setDoc(doc(collection(firestore, 'users'), userCredentials.user.uid), {
            id: userCredentials.user.uid,
            displayName: userCredentials.user.displayName,
            email: userCredentials.user.email,
            photoURL: userCredentials.user.photoURL,
            usage: 0,
            lastLoginDate: new Date().toISOString()
        });
        setCurrentStep(1);
    };

    if (finalMessage) {
        return (
             <div className="flex h-full w-full items-center justify-center  min-h-[80vh]">
                <div className="flex w-full max-w-sm flex-col gap-4 rounded-large bg-content1 px-8 pb-10 pt-6 shadow-small">
                    <p className="pb-2 text-xl font-medium">Thank you</p>
                    <div className="text-sm"> {finalMessage} </div>
                </div>
            </div>
        )
    }

    if (currentStep === 1) {
        return (
            <div className="flex h-full w-full items-center justify-center  min-h-[80vh]">
                <div className="flex w-full max-w-sm flex-col gap-4 rounded-large bg-content1 px-8 pb-10 pt-6 shadow-small">
                    <p className="pb-2 text-xl font-medium">Tell me about you</p>
                    {message && <p className="text-red-500 text-sm">{message}</p>}
                    <form className="flex flex-col gap-4" onSubmit={(e) => e.preventDefault()}>
                        <Input
                            isRequired
                            label="Name"
                            labelPlacement="outside"
                            name="name"
                            placeholder="Enter your name"
                            type="text"
                            variant="bordered"
                            onChange={(e) => setName(e.target.value)}
                        />
                        <Input
                            isRequired
                            label="Company"
                            labelPlacement="outside"
                            name="company"
                            placeholder="Enter your company"
                            type="text"
                            variant="bordered"
                            onChange={(e) => setCompany(e.target.value)}
                        />
                        <Input
                            isRequired
                            label="Job Title"
                            labelPlacement="outside"
                            name="jobTitle"
                            placeholder="Enter your job title"
                            type="text"
                            variant="bordered"
                            onChange={(e) => setJobTitle(e.target.value)}
                        />
                        <Button color="primary" type="submit" onClick={() => saveProfile()}>
                            Save and Continue
                        </Button>
                    </form>
                </div>
            </div>
        )
    }

    if (currentStep === 0) {
        // Check if we're in development mode
        const isDevelopment = !process.env.NODE_ENV || process.env.NODE_ENV === 'development';
        
        if (!isDevelopment) {
            // Production: Show disabled message
            return (
                <div className="flex h-full w-full items-center justify-center  min-h-[80vh]">
                    <div className="flex w-full max-w-sm flex-col gap-4 rounded-large bg-content1 px-8 pb-10 pt-6 shadow-small">
                        <p className="pb-2 text-xl font-medium">Sign ups are currently disabled</p>
                        <p className="text-sm text-default-500">
                            New user registrations are temporarily unavailable. Please check back later or contact support for more information.
                        </p>
                        <p className="text-center text-small pt-4">
                            Already have an account?&nbsp;
                            <Link href="/login" size="sm" >
                                Login
                            </Link>
                        </p>
                    </div>
                </div>
            );
        }
        
        // Development: Show signup form
        return (
            <div className="flex h-full w-full items-center justify-center  min-h-[80vh]">
                <div className="flex w-full max-w-sm flex-col gap-4 rounded-large bg-content1 px-8 pb-10 pt-6 shadow-small">
                    <p className="pb-2 text-xl font-medium">Sign up for DoxPro</p>
                    {message && <p className="text-red-500 text-sm">{message}</p>}
                    <form className="flex flex-col gap-3" onSubmit={(e) => {e.preventDefault(); onSignup()}}>
                        <Input
                            isRequired
                            label="Email Address"
                            name="email"
                            placeholder="Enter your email"
                            type="email"
                            variant="bordered"
                            onChange={(e) => setEmail(e.target.value)}
                            value={email}
                        />
                        <Input
                            isRequired
                            endContent={
                                <button type="button" onClick={toggleVisibility}>
                                    {isVisible ? (
                                        <Icon
                                            className="pointer-events-none text-2xl text-default-400"
                                            icon="solar:eye-closed-linear"
                                        />
                                    ) : (
                                        <Icon
                                            className="pointer-events-none text-2xl text-default-400"
                                            icon="solar:eye-bold"
                                        />
                                    )}
                                </button>
                            }
                            label="Password"
                            name="password"
                            placeholder="Enter your password"
                            type={isVisible ? "text" : "password"}
                            variant="bordered"
                            onChange={(e) => setPassword(e.target.value)}
                            value={password}
                        />
                        <Input
                            isRequired
                            endContent={
                                <button type="button" onClick={toggleConfirmVisibility}>
                                    {isConfirmVisible ? (
                                        <Icon
                                            className="pointer-events-none text-2xl text-default-400"
                                            icon="solar:eye-closed-linear"
                                        />
                                    ) : (
                                        <Icon
                                            className="pointer-events-none text-2xl text-default-400"
                                            icon="solar:eye-bold"
                                        />
                                    )}
                                </button>
                            }
                            label="Confirm Password"
                            name="confirmPassword"
                            placeholder="Confirm your password"
                            type={isConfirmVisible ? "text" : "password"}
                            variant="bordered"
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            value={confirmPassword}
                        />
                        <Checkbox 
                            isSelected={agreedToTerms}
                            onValueChange={setAgreedToTerms}
                        >
                            I agree to the terms and conditions
                        </Checkbox>
                        <Button color="primary" type="submit">
                            Sign Up
                        </Button>
                    </form>
                    <div className="flex items-center gap-4 py-2">
                        <Divider className="flex-1" />
                        <p className="shrink-0 text-tiny text-default-500">OR</p>
                        <Divider className="flex-1" />
                    </div>
                    <div className="flex flex-col gap-2">
                        <Button
                            startContent={<Icon icon="flat-color-icons:google" width={24} />}
                            variant="bordered"
                            onClick={() => signInWithGoogle(auth)}
                        >
                            Continue with Google
                        </Button>
                    </div>
                    <p className="text-center text-small pt-4">
                        Already have an account?&nbsp;
                        <Link href="/login" size="sm" >
                            Login
                        </Link>
                    </p>
                </div>
            </div>
        );
    }

    return <></>
}